import config from '../../config/config';
import {
  LocationParameters,
  LocationStatusChangeParameters,
  StationParameters,
  StationStatusChangeParameters,
} from '../../interface/charging-location.interface';

import deleteRequest from './base/delete';
import getRequest from './base/get';
import patchRequest from './base/patch';
import postRequest from './base/post';

interface QueryParameters {
  limit?: number;
  [key: string]: any;
}

const chargingLocationApi = {
  /**
   * Fetch the charging location list with pagination.
   * @param {QueryParameters} [query] - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the charging locations.
   */
  fetchChargingLocations(query: QueryParameters = {}): Promise<any> {
    const parameters = { ...query };
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/charging-station`, {
      ...parameters,
    });
  },

  /**
   * Fetch charging location list filters.
   * @returns {Promise<any>} A promise that resolves with the list filters.
   */
  fetchChargingLocationsListFilters(): Promise<any> {
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/charging-station/list-filters`);
  },

  /**
   * Fetch charging location by Id.
   * @param {string} id - The charging location ID.
   * @returns {Promise<any>} A promise that resolves with the charging location.
   */
  fetchChargingLocationById(id: string): Promise<any> {
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/charging-station/${id}`);
  },

  /**
   * Export the charging location list.
   * @param {QueryParameters} [query] - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the exported data.
   */
  exportChargingLocations(query: QueryParameters = {}): Promise<any> {
    const parameters = { ...query };
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/charging-station/export-data`, {
      ...parameters,
    });
  },

  /**
   * Create the location api call function.
   * @param {LocationParameters} data - The location parameters.
   * @returns {Promise<any>} A promise that resolves with the creation response.
   */
  createChargingLocation(
    data: LocationParameters = {
      name: '',
      email: '',
      role: '',
      password: '',
    },
  ): Promise<any> {
    const parameters = { ...data };
    return postRequest(`${config.commonApiUrl}api/super-admin/v1/users`, {
      ...parameters,
    });
  },

  /**
   * Update the location api call function.
   * @param {string} id - The charging location ID.
   * @param {LocationParameters} data - The location parameters.
   * @returns {Promise<any>} A promise that resolves with the update response.
   */
  editChargingLocation(
    id: string,
    data: LocationParameters = {
      name: '',
      email: '',
      role: '',
    },
  ): Promise<any> {
    const parameters = { ...data };
    return patchRequest(`${config.commonApiUrl}api/super-admin/v1/users/${id}`, {
      ...parameters,
    });
  },

  /**
   * Delete the location api call function.
   * @param {string} id - The charging location ID.
   * @returns {Promise<any>} A promise that resolves with the deletion response.
   */
  deleteChargingLocation(id: string): Promise<any> {
    return deleteRequest(`${config.commonApiUrl}api/super-admin/v1/users/${id}`);
  },

  /**
   * Status change for charging location.
   * @param {string} id - The charging location ID.
   * @param {LocationStatusChangeParameters} data - The status change parameters.
   * @returns {Promise<any>} A promise that resolves with the status change response.
   */
  statusChangeChargingLocation(id: string, data: LocationStatusChangeParameters): Promise<any> {
    const parameters = { ...data };
    return patchRequest(`${config.commonApiUrl}api/super-admin/v1/charging-station/${id}`, {
      ...parameters,
    });
  },

  /**
   * Fetch charging points by location Id.
   * @param {string} id - The charging location ID.
   * @returns {Promise<any>} A promise that resolves with the charging points.
   */
  fetchChargingPointByLocationId(id: string): Promise<any> {
    return getRequest(
      `${config.commonApiUrl}api/super-admin/v1/charging-point/charging-station/${id}`,
    );
  },

  /**
   * Pending notification count.
   * @returns {Promise<any>} A promise that resolves with the pending notification count.
   */
  fetchPendingNotificationCount(): Promise<any> {
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/charging-station/pending-count`);
  },

  /**
   * Delete device status / schedule.
   * @param {string} id - The device/location ID.
   * @returns {Promise<any>} A promise that resolves with the deletion response.
   */
  deleteDeviceStatus(id: string): Promise<any> {
    return deleteRequest(
      `${config.commonApiUrl}api/super-admin/v1/charging-station/cancel-schedule-delete-station/${id}`,
    );
  },

  /**
   * Check if the location being deleted has active charging points.
   * @param {string} id - The location ID to check.
   * @returns {Promise<any>} A promise that resolves with the deletion check response.
   */
  getLocationDeletionCheck(id: string): Promise<any> {
    return getRequest(
      `${config.commonApiUrl}api/super-admin/v1/charging-station/deletion-check/${id}`,
    );
  },

  /**
   * Manually syncs charging location data with the OCPI gateway.
   * @returns {Promise<any>} A promise that resolves with the sync response.
   */
  syncOcpiChargingLocations(): Promise<any> {
    return postRequest(`${config.commonApiUrl}api/ocpi/v1/charging-station/sync`, {});
  },

  /**
   * Fetch charging stations (backward compatibility alias).
   * @param {QueryParameters} [query] - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the charging stations.
   */
  fetchChargingStations(query: QueryParameters = {}): Promise<any> {
    return this.fetchChargingLocations(query);
  },

  /**
   * Fetch charging stations list filters (backward compatibility alias).
   * @returns {Promise<any>} A promise that resolves with the filters.
   */
  fetchChargingStationsListFilters(): Promise<any> {
    return this.fetchChargingLocationsListFilters();
  },

  /**
   * Fetch charging station by Id (backward compatibility alias).
   * @param {string} id - The station ID.
   * @returns {Promise<any>} A promise that resolves with the station.
   */
  fetchChargingStationById(id: string): Promise<any> {
    return this.fetchChargingLocationById(id);
  },

  /**
   * Export charging stations (backward compatibility alias).
   * @param {QueryParameters} [query] - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the exported data.
   */
  exportChargingStations(query: QueryParameters = {}): Promise<any> {
    return this.exportChargingLocations(query);
  },

  /**
   * Create charging station (backward compatibility alias).
   * @param {StationParameters} data - The station parameters.
   * @returns {Promise<any>} A promise that resolves with the response.
   */
  createChargingStation(data: StationParameters): Promise<any> {
    return this.createChargingLocation(data);
  },

  /**
   * Edit charging station (backward compatibility alias).
   * @param {string} id - The station ID.
   * @param {StationParameters} data - The station parameters.
   * @returns {Promise<any>} A promise that resolves with the response.
   */
  editChargingStation(id: string, data: StationParameters): Promise<any> {
    return this.editChargingLocation(id, data);
  },

  /**
   * Delete charging station (backward compatibility alias).
   * @param {string} id - The station ID.
   * @returns {Promise<any>} A promise that resolves with the response.
   */
  deleteChargingStation(id: string): Promise<any> {
    return this.deleteChargingLocation(id);
  },

  /**
   * Status change charging station (backward compatibility alias).
   * @param {string} id - The station ID.
   * @param {StationStatusChangeParameters} data - The status parameters.
   * @returns {Promise<any>} A promise that resolves with the response.
   */
  statusChangeChargingStation(id: string, data: StationStatusChangeParameters): Promise<any> {
    return this.statusChangeChargingLocation(id, data);
  },

  /**
   * Fetch charging points by station Id (backward compatibility alias).
   * @param {string} id - The station ID.
   * @returns {Promise<any>} A promise that resolves with the charging points.
   */
  fetchChargingPointByStationId(id: string): Promise<any> {
    return this.fetchChargingPointByLocationId(id);
  },

  /**
   * Get station deletion check (backward compatibility alias).
   * @param {string} id - The station ID.
   * @returns {Promise<any>} A promise that resolves with the response.
   */
  getStationDeletionCheck(id: string): Promise<any> {
    return this.getLocationDeletionCheck(id);
  },

  /**
   * Sync OCPI charging stations (backward compatibility alias).
   * @returns {Promise<any>} A promise that resolves with the response.
   */
  syncOcpiChargingStations(): Promise<any> {
    return this.syncOcpiChargingLocations();
  },
};

export default chargingLocationApi;
